// ==========================================================================
// lib/networth.ts — family net worth (10 Oct 2026)
//   James: "Family net worth on Home … lets go onto number 2".
//   Adds up what the app already knows, split by legal owner:
//     cash      bank, savings, offset and cash accounts (latest statement
//               balance, or opening balance plus lines); joint accounts 50/50
//     property  each property's value estimate × each owner's share
//     shares    each holding × its last price (cost when there's no price)
//     debts     loans owing and credit card balances
//     vehicles  owned or on a loan at their value estimate (a novated lease
//               isn't ours until the residual is paid)
//   Company accounts (external owners) are left out. Anything without a
//   number is listed as missing, never guessed.
// ==========================================================================
import { useMemo } from "react";
import { useEntities } from "./household";
import { useAccounts } from "./money";
import { useRecSummary } from "./reconcile";
import { balanceOf } from "./loans";
import { useProperties } from "./property";
import { useShares, workOut } from "./shares";
import { useVehicles } from "./vehicles";
import { aggregateState } from "./aggregateState";

export type NwCategory = "cash" | "property" | "shares" | "vehicles" | "debts";
export const NW_LABEL: Record<NwCategory, string> = { cash: "Cash and savings", property: "Property", shares: "Shares", vehicles: "Vehicles", debts: "Loans and cards" };
export type NwItem = { key: string; category: NwCategory; name: string; owner: string; cents: number; note?: string };

export function useNetWorth() {
  const entities = useEntities(); const accounts = useAccounts(); const summary = useRecSummary();
  const props = useProperties(); const shares = useShares(); const vehicles = useVehicles();
  const sources = [entities, accounts, summary, props, shares, vehicles];
  const labels = ["Owners", "Accounts", "Balances", "Property and ownership", "Investments", "Vehicles"];
  const state = aggregateState(sources.map((q, i) => ({ ...q, label: labels[i] })));

  const data = useMemo(() => {
    const ents = entities.data ?? [];
    const ours = (id: string | null | undefined) => !!id && ents.some((e) => e.id === id && !e.is_external);
    const items: NwItem[] = []; const missing: { name: string; why: string }[] = [];

    for (const a of accounts.data ?? []) {
      if (a.closed_on || a.kind === "broker" || !ours(a.owner_entity_id)) continue;
      const b = balanceOf(a, summary.data);
      if (!b) { missing.push({ name: a.name, why: "no balance yet" }); continue; }
      const debt = a.kind === "loan" || a.kind === "credit_card";
      const cents = debt ? -Math.abs(b.cents) : b.cents;
      const owners = a.co_owner_entity_id && ours(a.co_owner_entity_id) ? [a.owner_entity_id!, a.co_owner_entity_id] : [a.owner_entity_id!];
      for (const o of owners) items.push({ key: `a${a.id}${o}`, category: debt ? "debts" : "cash", name: a.name, owner: o, cents: Math.round(cents / owners.length), note: b.asAt ? `as at ${b.asAt}` : undefined });
    }

    for (const p of props.data?.props ?? []) {
      if (p.assets.disposed_on) continue;
      if (p.value_cents === null) { missing.push({ name: p.assets.name, why: "no value estimate" }); continue; }
      const own = (props.data?.owners ?? []).filter((o) => o.asset_id === p.asset_id && !o.valid_to && ours(o.owner_entity_id));
      for (const o of own) items.push({ key: `p${p.asset_id}${o.owner_entity_id}`, category: "property", name: p.assets.name, owner: o.owner_entity_id, cents: Math.round(p.value_cents * o.share_pct / 100), note: p.value_as_at ? `estimate ${p.value_as_at}` : "estimate" });
    }

    // Vehicles (024): owned or on a loan count at their estimate; a novated lease isn't ours until the residual is paid.
    for (const v of vehicles.data ?? []) {
      if (v.assets.disposed_on || v.finance === "novated") continue;
      if (v.value_cents === null) { missing.push({ name: v.assets.name, why: "no value estimate" }); continue; }
      const own = (props.data?.owners ?? []).filter((o) => o.asset_id === v.asset_id && !o.valid_to && ours(o.owner_entity_id));
      for (const o of own) items.push({ key: `v${v.asset_id}${o.owner_entity_id}`, category: "vehicles", name: v.assets.name, owner: o.owner_entity_id, cents: Math.round(v.value_cents * o.share_pct / 100), note: v.value_as_at ? `estimate ${v.value_as_at}` : "estimate" });
    }

    const sec = shares.data?.securities ?? [];
    for (const h of workOut(shares.data?.trades ?? []).holdings) {
      if (!ours(h.owner)) continue;
      const s = sec.find((x) => x.id === h.security);
      const priced = s?.last_price !== null && s?.last_price !== undefined;
      const cents = priced ? Math.round(h.units * s!.last_price! * 100) : h.costCents;
      if (!priced && h.unknownUnits) { missing.push({ name: s?.code ?? "A holding", why: "no price or cost" }); continue; }
      items.push({ key: `s${h.security}${h.owner}`, category: "shares", name: s?.code ?? "Shares", owner: h.owner, cents, note: priced ? (s!.last_price_on ? `price ${s!.last_price_on}` : undefined) : "at cost, no price" });
    }
    return { items, missing };
  }, [entities.data, accounts.data, summary.data, props.data, shares.data, vehicles.data]);

  return { ...data, items: state.available ? data.items : [], entities: (entities.data ?? []).filter((e) => !e.is_external), ...state,
    retry: () => Promise.all(sources.map((q) => q.refetch())) };
}

export const sumBy = (items: NwItem[], cat?: NwCategory) => items.filter((i) => !cat || i.category === cat).reduce((t, i) => t + i.cents, 0);
